import axios from 'axios'
import { axiosInstance } from './axiosInstance'
import { END_POINT_PRODUCT } from './index'

const SHARE_LINK_PATH = /^\/link\/([^/]+)/

export const isShareLinkPath = (pathname: string = window.location.pathname): boolean => SHARE_LINK_PATH.test(pathname)

const readStored = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

const store = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    // Without storage the token only lives in the URL; in-app navigation drops it.
  }
}

/**
 * The share token for the open /link page: from ?share= on arrival, then from the tab's session, because in-app
 * navigation rebuilds the URL without the query. Null off /link pages or for another project.
 */
export const shareTokenFor = (projectId?: string): string | null => {
  const match = window.location.pathname.match(SHARE_LINK_PATH)
  if (!match || (projectId && match[1] !== projectId)) return null
  const key = `shareToken:${match[1]}`
  const fromUrl = new URLSearchParams(window.location.search).get('share')
  if (fromUrl) {
    store(key, fromUrl)
    return fromUrl
  }
  return readStored(key)
}

// Bare axios: viewers are usually signed out, and a stale token would make the public endpoint reject the request.
const sharedUrl = (token: string, path = '') => `${END_POINT_PRODUCT}/public/shared/${encodeURIComponent(token)}${path}`

export const getSharedProject = (token: string) => axios.get(sharedUrl(token))

export const getSharedFolders = (token: string) => axios.get(sharedUrl(token, '/folders'))

export const getSharedDrawingSets = (token: string) => axios.get(sharedUrl(token, '/drawing-sets'))

export const getSharedPageAssemblies = (token: string, pageId: string) =>
  axios.get(sharedUrl(token, `/pages/${encodeURIComponent(pageId)}/assemblies`))

/** The project's read-only share token; the same one every time until the owner revokes it. */
export const createShareLink = async (projectId: string): Promise<string> => {
  const res = await axiosInstance.post<{ token: string }>(`${END_POINT_PRODUCT}/api/takeoffs/${projectId}/share-link`)
  return res.data.token
}

export const shareLinkUrl = (projectId: string, pageId: string, token: string): string =>
  `${window.location.origin}/link/${projectId}/${pageId}?share=${encodeURIComponent(token)}&utm_source=share-project`

/**
 * Copies the open sheet's share link. The clipboard write starts inside the click (Safari refuses writes after an
 * await) and fills in once the token arrives.
 */
export const copyShareLink = async (projectId: string): Promise<void> => {
  const pageId = window.location.pathname.split('/').pop() ?? ''
  const url = createShareLink(projectId).then((token) => shareLinkUrl(projectId, pageId, token))
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
    const blob = url.then((text) => new Blob([text], { type: 'text/plain' }))
    await navigator.clipboard.write([new ClipboardItem({ 'text/plain': blob })])
  } else {
    await navigator.clipboard.writeText(await url)
  }
}
